import { useCallback, useEffect, useMemo, useState } from 'react';
import storeLocationsService from '../services/storeLocationsService';
import type { StoreLocation } from '../../../../types/features/storeDetails/locations';

type StoreLocationGroup = {
  title: string;
  items: StoreLocation[];
};

const getGroupTitle = (location: StoreLocation): string => {
  if (location.is_default) {
    return 'Default Location';
  }

  if (location.location_type === 'vending') {
    return 'Vending Locations';
  }

  return 'Counter Locations';
};

const getGroupOrder = (title: string): number => {
  if (title === 'Default Location') return 0;
  if (title === 'Vending Locations') return 1;
  return 2;
};

const groupLocations = (locations: StoreLocation[]): StoreLocationGroup[] => {
  const groups = locations.reduce<Record<string, StoreLocation[]>>((accumulator, location) => {
    const title = getGroupTitle(location);
    const nextItems = accumulator[title] || [];
    return {
      ...accumulator,
      [title]: [...nextItems, location],
    };
  }, {});

  return Object.entries(groups)
    .map(([title, items]) => ({
      title,
      items,
    }))
    .sort((left, right) => getGroupOrder(left.title) - getGroupOrder(right.title));
};

export const useStoreLocations = (storeId: string) => {
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!storeId) {
      setLocations([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await storeLocationsService.getStoreLocations(storeId);
      setLocations(response);
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Failed to load locations.';
      setError(message);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const groupedLocations = useMemo(() => groupLocations(locations), [locations]);

  return {
    error,
    groupedLocations,
    locations,
    loading,
    loadLocations,
  };
};
